/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Precise Shaka-error-code → V-code mapping for the web player, sourced from
 * the "Player Error to V-Code Mapping" Confluence page (Web column).
 *
 * Unlike {@link getErrorCodeForCategory} in `PlayerErrorCodes`, which buckets
 * every Shaka error into one of eleven V9xx category codes, this table maps
 * each individual `shaka.util.Error.Code` to the same specific V-code
 * Android/ExoPlayer reports for the equivalent `PlaybackException`, so
 * support can look up one V-code across platforms regardless of which client
 * reported it.
 *
 * A Shaka code with no entry below — including the Cast/Storage/Ads/test-only
 * codes the source doc deliberately leaves unmapped — resolves to
 * {@link DEFAULT_WEB_ERROR_CODE}.
 */

/** Fallback V-code for a Shaka error with no specific entry below. */
export const DEFAULT_WEB_ERROR_CODE = "V555";

/**
 * shaka.net.NetworkingEngine.RequestType values needed to disambiguate a
 * network error by fetch stage. Sourced from
 * shaka.net.NetworkingEngine.RequestType (Shaka Player 4.7.0). Read via
 * {@link ShakaPlayerUtils.getRequestType}.
 */
const RequestType = {
  MANIFEST: 0,
  SEGMENT: 1,
  LICENSE: 2,
  SERVER_CERTIFICATE: 5
} as const;

/** shaka.util.Error.Code values the doc distinguishes by more than a straight lookup. */
const AmbiguousShakaCode = {
  BAD_HTTP_STATUS: 1001,
  HTTP_ERROR: 1002,
  TIMEOUT: 1003,
  VIDEO_ERROR: 3016
} as const;

/**
 * HTML `MediaError.code` values, read from a VIDEO_ERROR's `error.data[0]`
 * via {@link ShakaPlayerUtils.getMediaErrorCode}.
 */
const MediaErrorCode = {
  NETWORK: 2,
  DECODE: 3,
  SRC_NOT_SUPPORTED: 4
} as const;

/**
 * Every distinct V-code {@link getWebErrorCode} can produce — both the
 * one-to-one entries in {@link SHAKA_CODE_TO_V_CODE} and the fetch-stage-/
 * MediaError-dependent codes {@link resolveBadHttpStatus},
 * {@link resolveConnectionFailure} and {@link resolveVideoError} return —
 * named by the `subject` field of its YAML definition in
 * https://github.com/tivocorp/unity-client-error-codes/tree/main/codes/V.
 *
 * Several codes in the same family carry an identical `subject` — V550/V551/
 * V555/V560/V565 are all "Player Error", V561-V564 are all "Decoder Error",
 * and V593/V594 are both "Player DRM Error" — so those are named after the
 * specific failure their YAML `trigger` describes instead, substituting
 * "PLAYER" for the ExoPlayer-specific "EXO" prefix the trigger uses.
 */
export const playerErrorCodes = {
  PLAYER_BAD_INPUT: "V550",
  PLAYER_PARSER_FAILURE: "V551",
  PLAYER_SOURCE_EXCEPTION: "V555",
  PLAYER_ILLEGAL_STATE: "V560",
  DECODER_QUERY_FAILED: "V561",
  DECODER_INITIALIZATION_FAILED: "V562",
  DECODER_CRYPTO_FAILED: "V563",
  SUBTITLE_DECODER_FAILED: "V564",
  PLAYER_RENDER_FAILED: "V565",
  PLAYER_DRM_OUTPUT_PROTECTION_ERROR: "V593",
  PLAYER_DRM_SECURITY_LEVEL_ERROR: "V594",
  DRM_DECRYPTION_KEY_MISSING: "V634",
  MANIFEST_SOCKET_OPEN_ERROR: "V635",
  MANIFEST_INVALID_HTTP_RESPONSE: "V636",
  SEGMENT_SOCKET_OPEN_ERROR: "V638",
  SEGMENT_INVALID_HTTP_RESPONSE: "V639",
  SOCKET_OPEN_EXCEPTION: "V641",
  INVALID_HTTP_RESPONSE: "V642",
  DRM_SYSTEM_ERROR: "V942",
  DRM_NOT_SUPPORTED_ERROR: "V945",
  DRM_CONTENT_ERROR: "V946",
  DRM_LICENSE_EXPIRED_ERROR: "V947",
  DRM_LICENSE_ACQUISITION_ERROR: "V948",
  DRM_UNSPECIFIED_ERROR: "V950"
} as const;

/**
 * Direct one-to-one `shaka.util.Error.Code` → V-code entries — every mapped
 * row of the doc whose Web column is not fetch-stage- or MediaError-
 * dependent (those live in {@link resolveAmbiguousShakaCode} instead).
 */
const SHAKA_CODE_TO_V_CODE: ReadonlyMap<number, string> = new Map([
  // V550 — source-path, unrecognised/unsupported container or manifest type.
  [2011, playerErrorCodes.PLAYER_BAD_INPUT],
  [3006, playerErrorCodes.PLAYER_BAD_INPUT],
  [3019, playerErrorCodes.PLAYER_BAD_INPUT],
  [4000, playerErrorCodes.PLAYER_BAD_INPUT],
  [4006, playerErrorCodes.PLAYER_BAD_INPUT],
  [4020, playerErrorCodes.PLAYER_BAD_INPUT],
  [4025, playerErrorCodes.PLAYER_BAD_INPUT],
  [4027, playerErrorCodes.PLAYER_BAD_INPUT],
  [4058, playerErrorCodes.PLAYER_BAD_INPUT],
  [4063, playerErrorCodes.PLAYER_BAD_INPUT],

  // V551 — source-path, malformed container/manifest.
  [3000, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3001, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3002, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3003, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3004, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3005, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3007, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3008, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3009, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3010, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3011, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3012, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3013, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [3018, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4001, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4002, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4003, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4004, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4005, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4010, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4015, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4016, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4017, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4018, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4023, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4024, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4028, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4036, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4037, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4039, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4050, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4052, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4061, playerErrorCodes.PLAYER_PARSER_FAILURE],
  [4062, playerErrorCodes.PLAYER_PARSER_FAILURE],

  // V555 — source-path catch-all.
  [4053, playerErrorCodes.PLAYER_SOURCE_EXCEPTION],

  // V560 — renderer-path IllegalStateException equivalents.
  [3015, playerErrorCodes.PLAYER_ILLEGAL_STATE],
  [5006, playerErrorCodes.PLAYER_ILLEGAL_STATE],
  [7002, playerErrorCodes.PLAYER_ILLEGAL_STATE],
  [7003, playerErrorCodes.PLAYER_ILLEGAL_STATE],
  [7004, playerErrorCodes.PLAYER_ILLEGAL_STATE],

  // V561 — renderer-path decoder query failure.
  [6001, playerErrorCodes.DECODER_QUERY_FAILED],

  // V562 — renderer-path decoder init / unsupported format.
  [4032, playerErrorCodes.DECODER_INITIALIZATION_FAILED],

  // V563 — renderer crypto path, no more specific Widevine mapping.
  [6003, playerErrorCodes.DECODER_CRYPTO_FAILED],

  // V564 — renderer-path subtitle decode failures.
  [2000, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2001, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2003, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2004, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2005, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2007, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2008, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2009, playerErrorCodes.SUBTITLE_DECODER_FAILED],
  [2010, playerErrorCodes.SUBTITLE_DECODER_FAILED],

  // V565 — renderer-path catch-all for decoding failures.
  [3014, playerErrorCodes.PLAYER_RENDER_FAILED],
  [3017, playerErrorCodes.PLAYER_RENDER_FAILED],

  // V593/V594 — renderer crypto path, HDCP/output protection.
  [6018, playerErrorCodes.PLAYER_DRM_OUTPUT_PROTECTION_ERROR],
  [4042, playerErrorCodes.PLAYER_DRM_SECURITY_LEVEL_ERROR],
  [6019, playerErrorCodes.PLAYER_DRM_SECURITY_LEVEL_ERROR],
  [6020, playerErrorCodes.PLAYER_DRM_SECURITY_LEVEL_ERROR],

  // V634 — renderer crypto path, offline session removed.
  [6013, playerErrorCodes.DRM_DECRYPTION_KEY_MISSING],

  // V642 — fetch error, generic/unknown stage.
  [1000, playerErrorCodes.INVALID_HTTP_RESPONSE],
  [1004, playerErrorCodes.INVALID_HTTP_RESPONSE],

  // V635 — fetch error (connection/timeout), manifest stage.
  [4064, playerErrorCodes.MANIFEST_SOCKET_OPEN_ERROR],

  // V639 — segment missing.
  [1011, playerErrorCodes.SEGMENT_INVALID_HTTP_RESPONSE],

  // V942 — fallback DRM system error (license/certificate connectivity).
  [6002, playerErrorCodes.DRM_SYSTEM_ERROR],
  [6005, playerErrorCodes.DRM_SYSTEM_ERROR],

  // V945 — fallback DRM scheme-unsupported.
  [4008, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4026, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4038, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4040, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4041, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4051, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [4054, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],
  [6000, playerErrorCodes.DRM_NOT_SUPPORTED_ERROR],

  // V946 — fallback DRM content error.
  [4007, playerErrorCodes.DRM_CONTENT_ERROR],
  [4048, playerErrorCodes.DRM_CONTENT_ERROR],
  [4049, playerErrorCodes.DRM_CONTENT_ERROR],
  [4059, playerErrorCodes.DRM_CONTENT_ERROR],
  [4060, playerErrorCodes.DRM_CONTENT_ERROR],
  [6004, playerErrorCodes.DRM_CONTENT_ERROR],
  [6006, playerErrorCodes.DRM_CONTENT_ERROR],
  [6008, playerErrorCodes.DRM_CONTENT_ERROR],
  [6016, playerErrorCodes.DRM_CONTENT_ERROR],

  // V947 — fallback DRM license expired.
  [6014, playerErrorCodes.DRM_LICENSE_EXPIRED_ERROR],

  // V948 — fallback DRM license acquisition failed.
  [6007, playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR],
  [6017, playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR],

  // V950 — fallback DRM unspecified.
  [6012, playerErrorCodes.DRM_UNSPECIFIED_ERROR],
  [6015, playerErrorCodes.DRM_UNSPECIFIED_ERROR]
]);

/** Resolve the fetch-stage-dependent V-code for BAD_HTTP_STATUS (1001). */
function resolveBadHttpStatus(requestType: number | undefined): string {
  switch (requestType) {
    case RequestType.MANIFEST:
      return playerErrorCodes.MANIFEST_INVALID_HTTP_RESPONSE;
    case RequestType.SEGMENT:
      return playerErrorCodes.SEGMENT_INVALID_HTTP_RESPONSE;
    case RequestType.LICENSE:
    case RequestType.SERVER_CERTIFICATE:
      return playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR;
    default:
      return playerErrorCodes.INVALID_HTTP_RESPONSE;
  }
}

/**
 * Resolve the fetch-stage-dependent V-code for HTTP_ERROR (1002) and
 * TIMEOUT (1003) — connection failures and timeouts share the same
 * per-stage V-codes.
 */
function resolveConnectionFailure(requestType: number | undefined): string {
  switch (requestType) {
    case RequestType.MANIFEST:
      return playerErrorCodes.MANIFEST_SOCKET_OPEN_ERROR;
    case RequestType.SEGMENT:
      return playerErrorCodes.SEGMENT_SOCKET_OPEN_ERROR;
    case RequestType.LICENSE:
    case RequestType.SERVER_CERTIFICATE:
      return playerErrorCodes.DRM_SYSTEM_ERROR;
    default:
      return playerErrorCodes.SOCKET_OPEN_EXCEPTION;
  }
}

/** Resolve the native-MediaError-dependent V-code for VIDEO_ERROR (3016). */
function resolveVideoError(mediaErrorCode: number | undefined): string {
  switch (mediaErrorCode) {
    case MediaErrorCode.SRC_NOT_SUPPORTED:
      return playerErrorCodes.DECODER_INITIALIZATION_FAILED;
    case MediaErrorCode.DECODE:
      return playerErrorCodes.PLAYER_RENDER_FAILED;
    case MediaErrorCode.NETWORK:
      return playerErrorCodes.SOCKET_OPEN_EXCEPTION;
    default:
      return DEFAULT_WEB_ERROR_CODE;
  }
}

/**
 * Resolve the V-code the "Player Error to V-Code Mapping" doc assigns to a
 * Shaka error, matching the same code Android/ExoPlayer reports for the
 * equivalent failure. Falls back to {@link DEFAULT_WEB_ERROR_CODE} for any
 * Shaka code the doc does not list.
 *
 * @param shakaCode - The numeric `shaka.util.Error.Code`.
 * @param requestType - `shaka.net.NetworkingEngine.RequestType` of the failed
 *   request, from {@link ShakaPlayerUtils.getRequestType}. Only consulted for
 *   BAD_HTTP_STATUS/HTTP_ERROR/TIMEOUT, which the doc distinguishes by fetch
 *   stage (manifest/segment/license/generic).
 * @param mediaErrorCode - The native `HTMLMediaElement.error.code`, from
 *   {@link ShakaPlayerUtils.getMediaErrorCode}. Only consulted for
 *   VIDEO_ERROR, which the doc distinguishes by the underlying MediaError.
 */
export function getWebErrorCode(
  shakaCode: number,
  requestType: number | undefined,
  mediaErrorCode: number | undefined
): string {
  switch (shakaCode) {
    case AmbiguousShakaCode.BAD_HTTP_STATUS:
      return resolveBadHttpStatus(requestType);
    case AmbiguousShakaCode.HTTP_ERROR:
    case AmbiguousShakaCode.TIMEOUT:
      return resolveConnectionFailure(requestType);
    case AmbiguousShakaCode.VIDEO_ERROR:
      return resolveVideoError(mediaErrorCode);
    default:
      return SHAKA_CODE_TO_V_CODE.get(shakaCode) || DEFAULT_WEB_ERROR_CODE;
  }
}

/**
 * Separator joining the category code and the sub-error code in a compound
 * player error code (e.g. `V951-001`). Mirrors kaveri's `LiveTvError.separator`.
 */
export const PLAYER_ERROR_SEPARATOR = "-";
